import {
  Button,
  HStack,
  IIconProps,
  Text,
  usePropsResolution,
  VStack,
} from 'native-base';
import React from 'react';
import { cloneElementWithProps } from '../../utils/component';
import {
  CloseOutlined,
  GetInfoCircleFilled,
  WarningCircleFilled,
  WarningTriangleFilled,
} from '../Icon';
import { IconButton } from '../IconButton';
import type { IBannerProps } from './Banner.types';

const colorSchemeIcons: { [key: string]: JSX.Element } = {
  info: <GetInfoCircleFilled />,
  warning: <WarningCircleFilled />,
  error: <WarningTriangleFilled />,
};

// Override these button variant's '_hover' styling.
const buttonVariantOverrides = ['outline', 'ghost'];

const Banner: React.FunctionComponent<IBannerProps> = ({
  buttons,
  colorScheme = 'info',
  description,
  onDismiss,
  showIcon,
  testID = 'Banner',
  title,
  ...props
}) => {
  const {
    _button,
    _buttonHover,
    _buttonPressed,
    _description,
    _dismiss,
    _icon,
    _title,
    ...resolvedProps
  } = usePropsResolution('Banner', { colorScheme, description, ...props });

  const styledIcon = React.useMemo(() => {
    const iconElement = showIcon
      ? typeof showIcon === 'boolean'
        ? colorSchemeIcons[colorScheme as string]
        : React.isValidElement(showIcon)
        ? showIcon
        : undefined
      : undefined;
    if (iconElement && React.isValidElement(iconElement)) {
      return cloneElementWithProps<IIconProps>(
        iconElement,
        _icon,
        'banner-icon'
      );
    }
    return undefined;
  }, [_icon, showIcon, colorScheme]);

  return (
    <HStack {...resolvedProps}>
      {styledIcon}
      <VStack flexGrow={1} flexShrink={1} space={2}>
        {title && <Text {..._title}>{title}</Text>}
        {description &&
          (typeof description === 'string' ? (
            <Text {..._description}>{description}</Text>
          ) : (
            description
          ))}
        {buttons && buttons.length > 0 && (
          <Button.Group>
            {buttons.map((buttonProps, index) => (
              <Button
                testID={`${testID}-Button-${index}`}
                {...buttonProps}
                {..._button}
                _hover={
                  buttonVariantOverrides.includes(buttonProps.variant as string)
                    ? _buttonHover
                    : undefined
                }
                _pressed={
                  buttonVariantOverrides.includes(buttonProps.variant as string)
                    ? _buttonPressed
                    : undefined
                }
              >
                {buttonProps.children}
              </Button>
            ))}
          </Button.Group>
        )}
      </VStack>
      {onDismiss && (
        <IconButton
          colorScheme={colorScheme}
          icon={<CloseOutlined />}
          onPress={onDismiss}
          size="xs"
          testID={`${testID}-IconButton-Dismiss`}
          variant="ghost"
          {..._dismiss}
        />
      )}
    </HStack>
  );
};

export default Banner;
